import { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { Plus, ArrowUpRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { VialImage } from "@/components/VialImage";

export default function ProductCard({ product, variants, title, index = 0 }) {
  const { add } = useCart();
  const list = variants && variants.length ? variants : [product];
  const hasSizes = list.length > 1 && list.every((v) => v._size);
  const [sel, setSel] = useState(0);
  const active = list[sel] || list[0];
  const displayTitle = hasSizes ? (title || active._base || active.name) : active.name;
  const soldOut = (active.stock ?? 0) <= 0;

  return (
    <div
      data-testid={`product-card-${active.id}`}
      className="group fade-up flex flex-col bg-surface border border-primary/20 hover:border-primary/60 transition-colors duration-300 hover:-translate-y-1 will-change-transform"
      style={{ animationDelay: `${index * 60}ms`, transition: "border-color 0.3s, transform 0.3s" }}
    >
      <Link to={`/product/${active.id}`} className="block relative overflow-hidden aspect-[4/5] bg-white">
        {active.image_url ? (
          <>
            <img src={active.image_url} alt={active.name} className="h-full w-full object-contain" />
            {active.bundle_items?.length > 0 && (
              <div className="absolute left-0 right-0 flex justify-around px-[9%] pointer-events-none" style={{ top: "53%", transform: "translateY(-50%)" }}>
                {active.bundle_items.slice(0, 3).map((it, i) => (
                  <span key={i} className="w-[26%] text-center font-bold uppercase leading-[1.05] text-[#6B5512]" style={{ fontFamily: "'Cinzel', serif", fontSize: "8px" }}>{it}</span>
                ))}
              </div>
            )}
          </>
        ) : (
          <VialImage name={active.name} />
        )}
        <span className="absolute top-3 left-3 label-mono border border-primary/30 text-primary bg-primary/10 px-2 py-1 text-[0.6rem]">
          {active.purity}
        </span>
        {active.top_seller && (
          <span data-testid={`top-seller-${active.id}`} className="absolute top-3 right-3 label-mono bg-primary text-white px-2 py-1 text-[0.6rem]">
            ★ Top Seller
          </span>
        )}
      </Link>
      <div className="p-5 flex flex-col flex-1">
        <span className="label-mono text-muted-foreground text-xs mb-2">{active.category}</span>
        <Link to={`/product/${active.id}`} className="font-head font-medium text-lg leading-tight hover:text-primary transition-colors flex items-start justify-between gap-2">
          {displayTitle}
          <ArrowUpRight size={16} className="text-muted-foreground shrink-0 mt-1" />
        </Link>

        {active.bundle_items?.length > 0 && (
          <p data-testid={`bundle-includes-${active.id}`} className="text-[0.65rem] text-muted-foreground mt-2 leading-snug">
            Includes: {active.bundle_items.join(" · ")}
          </p>
        )}

        {hasSizes && (
          <div data-testid={`size-select-${active.id}`} className="flex flex-wrap gap-1.5 mt-3">
            {list.map((v, i) => (
              <button
                key={v.id}
                data-testid={`size-opt-${v.id}`}
                onClick={() => setSel(i)}
                className={`label-mono text-[0.6rem] px-2.5 py-1 border transition-colors ${i === sel ? "border-primary text-primary bg-primary/10" : "border-black/15 text-muted-foreground hover:border-black/30"}`}
                style={{ transition: "color 0.2s, border-color 0.2s, background-color 0.2s" }}
              >
                {v._size}
              </button>
            ))}
          </div>
        )}

        <div className="mt-auto pt-5 flex items-center justify-between">
          <div className="flex flex-col">
            <div className="flex items-baseline gap-2">
              {hasSizes && sel === 0 && <span className="label-mono text-muted-foreground text-[0.6rem]">from</span>}
              <span className="font-mono text-xl text-foreground">${active.price?.toFixed(2)}</span>
              {active.compare_at_price > active.price && (
                <span className="font-mono text-sm text-muted-foreground line-through">${active.compare_at_price.toFixed(2)}</span>
              )}
            </div>
            {active.compare_at_price > active.price && (
              <span className="label-mono text-[0.55rem] text-primary mt-0.5">Save ${(active.compare_at_price - active.price).toFixed(2)}</span>
            )}
          </div>
          {soldOut ? (
            <span data-testid={`out-of-stock-${active.id}`} className="label-mono text-destructive border border-destructive/30 bg-destructive/5 px-3 py-2 text-[0.6rem]">
              Out of Stock
            </span>
          ) : (
            <button
              data-testid={`add-to-cart-${active.id}`}
              onClick={() => { add(active); toast.success(`${active.name} added to cart`); }}
              className="flex items-center gap-1 bg-primary text-black label-mono px-3 py-2 hover:bg-[#E6C766] transition-colors"
              style={{ transition: "background-color 0.2s" }}
            >
              <Plus size={14} weight="bold" /> Add
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
